/**
 * Inspector for the selected canvas node.
 *
 * Edits land in the in-memory draft and the canvas + issue panel re-derive on the next
 * render, so the author sees the consequence of a change immediately — that feedback
 * loop is the actual product, more than the picture is.
 *
 * Two honesty rules are enforced here rather than left to the reader:
 *
 *  1. A DERIVED node (an eligibility rule projected as a box) is not an editable
 *     record. Selecting one shows the owning question's rule and sends you to the
 *     owner to change it.
 *  2. `qualifiedNodeId` is the name every expression references. Renaming it silently
 *     breaks every rule that reads it, so the field shows exactly which rules point at
 *     the current value before you change it.
 */
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  ExpandableSection,
  FormField,
  Header,
  Input,
  Link,
  Select,
  SpaceBetween,
  StatusIndicator,
  Textarea,
} from '@amzn/awsui-components-react';

import type {
  QuestionnaireDefinition,
  QuestionnaireNode,
} from '../../types';
import type { CpaNode, Issue, QuestionKind } from '../../utils/projection';
import { PROGRAM_NAMES, metricRefsOf, questionKindOf } from '../../utils/projection';
import { UI_COMPONENTS, uiComponentSpec } from '../../utils/uiComponents';
import OptionsEditor from './OptionsEditor';

/** Short name per answer kind, for the 高级 section's heading. */
const KIND_LABEL: Record<QuestionKind, string> = {
  single: '单选',
  multi: '多选',
  number: '数值',
  text: '文本',
  custom: '组件',
};

export interface NodeInspectorProps {
  definition: QuestionnaireDefinition;
  selected: CpaNode | null;
  issues: readonly Issue[];
  readOnly: boolean;
  onPatchNode: (nodeId: string, patch: Partial<QuestionnaireNode>) => void;
  onSelectNode: (nodeId: string) => void;
  /**
   * Dismiss the panel. Present in BOTH modes — the panel takes 380px of canvas whether
   * or not you can edit, and clicking empty canvas to clear the selection is not a
   * thing anyone discovers.
   */
  onClose: () => void;
}


/**
 * Every rule in the questionnaire that reads `qualifiedNodeId`, with where it lives.
 *
 * Used for the rename warning. Scans all three rule families because all three read
 * the same `metric[...]` namespace — a rename that only accounted for navigation rules
 * would still silently kill eligibility rules, which outnumber them 27 to 4 in q_1_1.
 */
function referencesTo(definition: QuestionnaireDefinition, qualifiedId: string | undefined) {
  if (!qualifiedId) return [];
  const hits: { nodeId: string; kind: string; expression: string }[] = [];
  for (const n of definition.nodes) {
    const scan = (kind: string, expression: string) => {
      if (metricRefsOf(expression).includes(qualifiedId)) {
        hits.push({ nodeId: n.nodeId, kind, expression });
      }
    };
    (n.nextQuestionRules ?? []).forEach((r) => scan('条件边', r.expression));
    (n.programRules ?? []).forEach((r) => scan('资格规则', r.expression));
    (n.autoAnswerRules ?? []).forEach((r) => scan('自动作答', r.expression));
  }
  return hits;
}

export default function NodeInspector({
  definition,
  selected,
  issues,
  readOnly,
  onPatchNode,
  onSelectNode,
  onClose,
}: NodeInspectorProps) {
  if (!selected) {
    return (
      <Container
        header={
          <Header
            variant="h3"
            actions={<Button variant="icon" iconName="close" ariaLabel="关闭" onClick={onClose} />}
          >
            节点详情
          </Header>
        }
      >
        <Box color="text-status-inactive" textAlign="center" padding={{ vertical: 'xl' }}>
          在左侧画布上点一个节点
        </Box>
      </Container>
    );
  }

  // ── Derived node: a view of a rule, not a record ──────────────────────────
  if (selected.derived) {
    const owner = definition.nodes.find((n) => n.nodeId === selected.data.ownerNodeId);
    return (
      <Container
        header={
          <Header
            variant="h3"
            actions={<Button variant="icon" iconName="close" ariaLabel="关闭" onClick={onClose} />}
          >
            资格规则（派生节点）
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Alert type="info" header="这个方框不是一条记录">
            它是问题「{selected.data.ownerNodeId}」上第 {(selected.data.ruleIndex ?? 0) + 1} 条{' '}
            <code>programRules</code> 的投影。实际存储里只有问题节点，没有「资格标记」节点
            —— 要改就改它的归属问题。
          </Alert>
          <FormField label="命中后产出的投资项目">
            <SpaceBetween size="xxs">
              {(selected.data.rule?.values ?? []).map((v) => (
                <StatusIndicator
                  key={v}
                  type={PROGRAM_NAMES.includes(v) ? 'success' : 'error'}
                >
                  {v}
                </StatusIndicator>
              ))}
            </SpaceBetween>
          </FormField>
          <FormField label="命中条件（提交时求值）">
            <Box variant="code" fontSize="body-s">
              {selected.data.rule?.expression}
            </Box>
          </FormField>
          {owner && (
            <Link onFollow={() => onSelectNode(owner.nodeId)}>
              去编辑归属问题「{owner.nodeId}」
            </Link>
          )}
        </SpaceBetween>
      </Container>
    );
  }

  const node = selected.data.node!;
  const nodeIssues = issues.filter((i) => i.nodeId === node.nodeId);
  const refs = referencesTo(definition, node.qualifiedNodeId);
  const patch = (p: Partial<QuestionnaireNode>) => onPatchNode(node.nodeId, p);
  // Derived, never stored: the panel's shape follows the kind, and the kind follows
  // the node's own fields.
  const kind = questionKindOf(node);
  const spec = uiComponentSpec(node.uiComponent);

  return (
    <Container
      header={
        <Header
          variant="h3"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {node.isFirstNode && <StatusIndicator type="info">入口节点</StatusIndicator>}
              <Button variant="icon" iconName="close" ariaLabel="关闭" onClick={onClose} />
            </SpaceBetween>
          }
        >
          {/*
           * The KIND, not the node number.
           *
           * `Q1.1.1` told a reader nothing they needed while editing — the panel is
           * already about the node they clicked. What they do need is which kind of
           * question it is, because that is what decides the rest of the panel.
           * The id still appears wherever it identifies something: checker messages,
           * the delete-impact dialog, the out-edge map.
           */}
          {KIND_LABEL[kind]}题
        </Header>
      }
      footer={
        readOnly ? undefined : (
          /*
           * There is deliberately NO Save button here.
           *
           * The container header already owns one, and it means "publish to the
           * service". A second Save on this card would read as writing to the server
           * while only touching the draft — two commit concepts for one action. So the
           * card commits into the draft as you type, and says so, rather than leaving
           * the missing button to be read as an omission.
           */
          <Box color="text-body-secondary" fontSize="body-s">
            改动即时进入<b>草稿</b>（可用工具栏的「↩ 撤销」回退）。要让 BD 端看到，需要用右上角的
            <b>保存</b>发布。
          </Box>
        )
      }
    >
      <SpaceBetween size="l">
        {nodeIssues.length > 0 && (
          <SpaceBetween size="xs">
            {nodeIssues.map((i) => (
              <Alert key={i.id} type={i.severity === 'error' ? 'error' : 'warning'}>
                {i.message}
              </Alert>
            ))}
          </SpaceBetween>
        )}

        <FormField
          label="题干（中文）"
          description="BD 端主标题。英文为 Smithy 上的必填字段，中文可空。"
        >
          <Textarea
            value={node.questionText.cn ?? ''}
            disabled={readOnly}
            rows={2}
            onChange={({ detail }) =>
              patch({ questionText: { ...node.questionText, cn: detail.value } })
            }
          />
        </FormField>
        <FormField label="题干（English）">
          <Textarea
            value={node.questionText.en}
            disabled={readOnly}
            rows={2}
            onChange={({ detail }) =>
              patch({ questionText: { ...node.questionText, en: detail.value } })
            }
          />
        </FormField>

        {/*
         * From here down the panel is driven by the question KIND.
         *
         * Showing every field for every question is how this grew to twelve sections,
         * most of them irrelevant to whatever was selected. Each kind now shows only
         * what it has: a single- or multi-select has answers, a number has a unit and a
         * scale, a component has a name, and a text question has nothing at all.
         *
         * What is NOT here, deliberately: a dataType selector (the kind decides it),
         * a 终点 checkbox (`connectNodes` and `addBranch` clear `isLastNode`), an
         * out-edge map and an eligibility list (both belong to the edges), and min/max
         * (not in the model — the live 0–100 check on `Q_1_1_10` is hard-coded in the
         * BD frontend, so adding the control here would imply a field that does not
         * exist).
         */}
        {/* 单选 / 多选 — a question plus a list of answers. */}
        {(kind === 'single' || kind === 'multi') && (
          <FormField
            label={`答案选项 · ${node.answerOptions?.length ?? 0}`}
            description={
              kind === 'multi'
                ? 'BD 端可以多选，答案存成数组，规则用 .includes() 匹配。顺序就是列表里的顺序。'
                : 'BD 端只能选一个。value 是规则比较的那个值；顺序就是下拉里的顺序。'
            }
          >
            <OptionsEditor
              definition={definition}
              node={node}
              readOnly={readOnly}
              onChange={(answerOptions) => patch({ answerOptions })}
            />
          </FormField>
        )}

        {/*
         * 数值 — no options; what it needs instead is how the number is READ and how it
         * is STORED, and those are two different things.
         *
         * `unit` is a label and changes nothing. `scale` changes the data: the author
         * types 30 and the engine stores 0.3. Separate controls because conflating them
         * is the live `Q_1_1_10` trap, where the `%` exists only in the frontend and the
         * ratio only in the data with nothing saying so — a rule then has to read
         * `>= 0.3` while the界面 says 30.
         */}
        {kind === 'number' && (
          <ColumnLayout columns={2}>
            <FormField label="单位" description="只影响显示，例如 $K、%。不改变存储的值。">
              <Input
                value={node.unit ?? ''}
                disabled={readOnly}
                placeholder="（无）"
                onChange={({ detail }) => patch({ unit: detail.value || undefined })}
              />
            </FormField>
            <FormField
              label="存储倍率"
              description="存储值 = 输入值 × 倍率。百分比填 0.01（输入 30 → 存 0.3）；留空等于 1。"
              errorText={
                node.unit === '%' && (node.scale ?? 1) === 1
                  ? '单位是 % 但倍率是 1 —— 规则作者看到 30 就会写 >= 30，而引擎存的也是 30。确认这是你要的。'
                  : undefined
              }
            >
              <Input
                value={node.scale === undefined ? '' : String(node.scale)}
                disabled={readOnly}
                placeholder="1"
                onChange={({ detail }) => {
                  const v = detail.value.trim();
                  const n = Number(v);
                  patch({ scale: v === '' || !isFinite(n) || n === 1 ? undefined : n });
                }}
              />
            </FormField>
          </ColumnLayout>
        )}

        {/*
         * 组件 — the control is chosen by name, so the name is the only field. Registered
         * today: `sfdc-account`, which fetches the caller's own SFDC accounts and
         * validates a typed id against another service.
         */}
        {kind === 'custom' && (
          <SpaceBetween size="s">
            <FormField
              label="渲染组件"
              description="控件由这个名字决定，不由题型决定。"
            >
              <Select
                disabled={readOnly}
                selectedOption={{
                  value: node.uiComponent,
                  label: spec?.label ?? node.uiComponent ?? '（无）',
                }}
                options={[
                  { value: '', label: '（无 — 用题型决定的普通控件）' },
                  ...UI_COMPONENTS.map((c) => ({ value: c.name, label: c.label, description: c.name })),
                ]}
                onChange={({ detail }) =>
                  patch({ uiComponent: detail.selectedOption.value || undefined })
                }
              />
            </FormField>

            {/*
             * A name, not a list, used to be the whole control. The problem is what
             * happens to a typo: AgentHubPortal resolves the name through a registry
             * and renders the literal text `Custom component 'X' not found` when it
             * misses — a broken question that looks like content. A list of what exists
             * removes that failure, and a name the registry does not know is called out
             * rather than hidden.
             */}
            {node.uiComponent && !spec && (
              <Alert type="warning" header="这个组件名没有登记">
                BD 端按名字到注册表里找控件，找不到会原样渲染出{' '}
                <code>Custom component '{node.uiComponent}' not found</code> —— 一道看起来
                像内容的坏题。确认名字拼写，或选上面列表里的一个。
              </Alert>
            )}

            {/* What the admin actually needs before choosing: the behaviour, the
                service behind it, and how it fails. */}
            {spec && (
              <SpaceBetween size="xs">
                <FormField label="这个组件做什么">
                  <Box fontSize="body-s">{spec.behaviour}</Box>
                </FormField>
                {spec.dataSource && (
                  <FormField label="数据来源">
                    <Box fontSize="body-s" color="text-body-secondary">
                      {spec.dataSource}
                    </Box>
                  </FormField>
                )}
                {spec.validation && (
                  <FormField label="校验">
                    <Box fontSize="body-s" color="text-body-secondary">
                      {spec.validation}
                    </Box>
                  </FormField>
                )}
              </SpaceBetween>
            )}
          </SpaceBetween>
        )}

        {/* 文本 needs nothing beyond its wording — stated so the panel does not look
            unfinished when it is simply short. */}
        {kind === 'text' && (
          <Box color="text-body-secondary" fontSize="body-s">
            自由文本题没有选项，也没有单位 —— 题干就是它的全部配置。
          </Box>
        )}

        {/*
         * The one field kept behind a fold, for every kind.
         *
         * `qualifiedNodeId` is the name every rule references. Renaming it on an
         * existing question silently breaks all of them: the expressions survive, read
         * `undefined`, and never match again. So it must be reachable — a freshly
         * created node is named `NEW_1` — but it must not sit in the normal flow of
         * editing where it gets changed by accident.
         */}
        <ExpandableSection headerText="高级">
          <FormField
            label="规则里引用的名字"
            description="表达式里 metric[...] 用的就是这个。"
            warningText={
              refs.length > 0
                ? `有 ${refs.length} 条规则按当前值引用它。改名会让这些规则再也匹配不上 —— 引擎不会报错，只是永远不命中。`
                : undefined
            }
          >
            <Input
              value={node.qualifiedNodeId ?? ''}
              disabled={readOnly}
              invalid={!node.qualifiedNodeId}
              onChange={({ detail }) => patch({ qualifiedNodeId: detail.value })}
            />
          </FormField>
        </ExpandableSection>
      </SpaceBetween>
    </Container>
  );
}
